import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDrawer } from '@angular/material/sidenav';
import {
  AccountService,
  formatDb2Timestamp,
} from '@jfw/angular/shared/services';
import {
  LegacyComment,
  LegacyCommentCategory,
  ModuleSuiteEnum,
} from '@jfw/types';
import { AuthService } from 'jfw-platform-portal/src/app/standalone/services/auth/auth.service';

// EXT-6537: sections in the dashboard design's order -- Customer Service,
// Calls, Accounting, Account. Sales (Salesforce rows, prod only) sits beside
// Customer Service rather than vanishing, and the warehouse's Service Center
// board (EXT-6530) follows Account. Anything else falls after these, keeping
// its original (query) order. A category with no rows never arrives.
const CATEGORY_SORT_ORDER = [
  'Customer Service',
  'Call Log',
  'Sales',
  'Accounting',
  'Account',
  'Service Center',
];

// Display names that differ from the query's category value.
const CATEGORY_LABELS: Record<string, string> = {
  'Call Log': 'Calls',
};

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  imports: [ReactiveFormsModule, MatButtonModule],
})
export class CommentsComponent implements OnInit {
  private accountService: AccountService = inject(AccountService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  @Input() drawer?: MatDrawer;

  // EXT-6505: Store Contact starts from the account bar's Caller Name, since
  // that is who the agent is talking to. This component remounts every time
  // the Comments tab opens, so the initial value is always current.
  private readonly callerName = this.accountService.callerName;

  commentForm: FormGroup = this.fb.group({
    text: ['', [Validators.required, Validators.minLength(1)]],
    storeContact: [this.callerName()],
  });

  constructor() {
    // EXT-6505: a Caller Name typed while the tab is already open flows in
    // too, but never over a Store Contact the agent has typed themselves.
    effect(() => {
      const caller = this.callerName();
      const control = this.commentForm.controls['storeContact'];
      if (!control.dirty && control.value !== caller) {
        control.setValue(caller);
      }
    });
  }

  readonly COMMENTS_PREVIEW_COUNT = 1;

  platformAccount = this.accountService._platformAccount;
  drawerState = computed(() => this.accountService.drawerState());
  comments = this.accountService._comments;

  // Which portal suite the drawer is currently open in — determines the category a
  // new comment lands in server-side (see comments.service.ts's CATEGORY_COLUMNS_BY_SUITE).
  // No user-facing category picker per explicit direction: the entry point (Wholesale vs.
  // Accounting) already tells us which category is correct.
  private readonly currentSuite = toSignal(this.authService.currentSuite$);

  sortedComments = computed(() => {
    const categories = this.comments();
    return [...categories].sort((a, b) => {
      const rankA = CATEGORY_SORT_ORDER.indexOf(a.category);
      const rankB = CATEGORY_SORT_ORDER.indexOf(b.category);
      const orderA = rankA === -1 ? CATEGORY_SORT_ORDER.length : rankA;
      const orderB = rankB === -1 ? CATEGORY_SORT_ORDER.length : rankB;
      return orderA - orderB;
    });
  });

  private expandedCategories = signal<Set<string>>(new Set());

  isExpanded(category: string): boolean {
    return this.expandedCategories().has(category);
  }

  toggleExpanded(category: string): void {
    this.expandedCategories.update((expanded) => {
      const next = new Set(expanded);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }

  visibleComments(category: LegacyCommentCategory): LegacyComment[] {
    return this.isExpanded(category.category)
      ? category.comments
      : category.comments.slice(0, this.COMMENTS_PREVIEW_COUNT);
  }

  formatDateTime(dateTime: string): string {
    return formatDb2Timestamp(dateTime);
  }

  // Only the comment's own author may edit/delete it — mirrors the ownership check
  // the backend independently enforces (comments.query.ts), so this UI gate is a
  // convenience, not the only line of defense.
  isOwnComment(comment: LegacyComment): boolean {
    return comment.user === this.authService.platformUser()?.displayName;
  }

  // EXT-6537: in the drawer, Edit/Delete appear only on the user's own CSR
  // (Customer Service) notes -- never on calls, accounting notes or the
  // account/warehouse boards, which the Dashboard's own sections manage.
  canEdit(category: string, comment: LegacyComment): boolean {
    return category === 'Customer Service' && this.isOwnComment(comment);
  }

  categoryLabel(category: string): string {
    return CATEGORY_LABELS[category] ?? category;
  }

  private editingId = signal<number | null>(null);
  private editDraft = signal<string>('');

  isEditing(comment: LegacyComment): boolean {
    return this.editingId() === comment.id;
  }

  editDraftText(): string {
    return this.editDraft();
  }

  onEditDraftChange(value: string): void {
    this.editDraft.set(value);
  }

  startEdit(comment: LegacyComment): void {
    this.editingId.set(comment.id);
    this.editDraft.set(comment.commentText);
  }

  cancelEdit(): void {
    this.editingId.set(null);
    this.editDraft.set('');
  }

  async saveEdit(comment: LegacyComment): Promise<void> {
    const text = this.editDraft().trim();
    if (!text) return;
    const { displayName, email } = this.authService.platformUser()!;
    await this.accountService.updateComment(
      comment.id,
      text,
      displayName,
      email,
    );
    this.cancelEdit();
  }

  async deleteComment(comment: LegacyComment): Promise<void> {
    const { displayName, email } = this.authService.platformUser()!;
    await this.accountService.deleteComment(comment.id, displayName, email);
  }

  ngOnInit(): void {
    const currentAccount = this.accountService.platformAccount;

    if (currentAccount?.accountNumber) {
      this.accountService.loadComments(currentAccount.accountNumber);
    } else {
      console.warn('No platform account selected; cannot load comments.');
    }
  }

  async addComment() {
    if (this.commentForm.valid) {
      const text = this.commentForm.value.text;
      const storeContact = this.commentForm.value.storeContact || '';
      const { displayName, email } = this.authService.platformUser()!;
      const suite = this.currentSuite()?.enum as ModuleSuiteEnum | undefined;
      // email lets the backend stamp the comment with the user's KMS login, without
      // which EZTux's dashboard won't show it (EXT-6337).
      await this.accountService.addComment(
        text,
        displayName,
        storeContact,
        suite,
        email,
      );

      // Reset the form and the validation state, keeping Store Contact
      // pre-filled from the caller for the next comment (EXT-6505).
      this.commentForm.reset({ text: '', storeContact: this.callerName() });
    }
  }
}
